import { css } from '@emotion/react'
import { memo, useCallback, useRef } from 'react'
import { openMenuDialog } from '../../../../actions/menu'
import { openMessage } from '../../../../actions/message'
import { openRoom, pinRoom, togglePinRoomChat } from '../../../../actions/room'
import { addFriend, bookmarkModel } from '../../../../actions/user'
import { setFullScreenExpandedState } from '../../../../app-state/modals'
import {
    openAuthenticationDrawer,
    openFriendConfirmationDrawer,
    openNotesDrawer,
    openTipsDrawer,
    openTopicDrawer,
} from '../../../../app-state/modals/state.drawer'
import {
    closeFloater,
    minimizeFloater,
    toggleQuickReplySize,
} from '../../../../app-state/modals/state.floater'
import { AppStateType, ConfirmationType } from '../../../../constants/enums'
import { QUICK_REPLY } from '../../../../constants/floaters'
import i18n from '../../../../utilities/i18n'
import { safeObj } from '../../../../utilities/safe'
import {
    TransitionTypes,
    motionTransition,
} from '../../../../utilities/transitions'
import ButtonShareTip from '../../../Buttons/ButtonShareTip'
import ButtonTipText from '../../../Buttons/ButtonTipText'
import ButtonX from '../../../Buttons/ButtonX'
import IconButton from '../../../Buttons/IconButton'
import { useAppState } from '../../../Hooks/useAppState'
import { useElementSize } from '../../../Hooks/useElementSize'
import { useFloatingRoomStateSelector } from '../../../Hooks/useModal'
import { useModelColor } from '../../../Hooks/useModelColor'
import {
    useIsRoomPinned,
    usePinnedRoomCount,
    useRoomSelector,
} from '../../../Hooks/useRoom'
import { useScreenStateSelector } from '../../../Hooks/useScreenState'
import { useUserSelector } from '../../../Hooks/useUser'
import AddBookmark from '../../../Icons/AddBookmark'
import AddFriend from '../../../Icons/AddFriend'
import ArrowCollapseLeft from '../../../Icons/ArrowCollapseLeft'
import ArrowExpandRight from '../../../Icons/ArrowExpandRight'
import Bookmarked from '../../../Icons/Bookmarked'
import ChevronDownIcon from '../../../Icons/ChevronDown'
import CloseIcon from '../../../Icons/Close'
import Friended from '../../../Icons/Friended'
import HideChat from '../../../Icons/HideChat'
import MessageIcon from '../../../Icons/Message'
import Notes from '../../../Icons/Notes'
import RoomIcon from '../../../Icons/Room'
import ShowChat from '../../../Icons/ShowChat'
import TipIcon from '../../../Icons/Tip'
import Videocam from '../../../Icons/Videocam'
import BadgeAvatar from '../../../common/BadgeAvatar'
import { getThemeSpacing } from '../../../styles/appTheme'
import APP_BREAKPOINTS, {
    maxPinWidth,
    maxRoomWidth,
    maxWindowWidth,
    minWindowWidth,
} from '../../../styles/breakpoints'
import { palette } from '../../../styles/colors'
import shape from '../../../styles/shape'
import {
    COMPONENT_HEIGHTS,
    PINNED_ROOM_MIN_WIDTH_FOR_CHAT,
} from '../../../theme'
import CounterViewers from '../../CounterViewers'
import PinButton from '../../Grid/Buttons/PinButton'

const SHARE_IMAGE_WIDTH = 70
const INFO_CONTAINER_SHIFT = 3

const rootStyles = css({
    display: 'flex',
    flexFlow: 'row nowrap',
    position: 'relative',
    zIndex: 1,
    height: COMPONENT_HEIGHTS.chatHeader,
    minHeight: COMPONENT_HEIGHTS.chatHeader,
    maxHeight: COMPONENT_HEIGHTS.chatHeader,
    padding: `${getThemeSpacing(0.125)} ${getThemeSpacing(
        0.5,
    )} ${getThemeSpacing(0.375)}`,
})

const rootPreviewStyles = css({
    height: 'unset',
    cursor: 'grab',
    borderTopRightRadius: shape.borderRadius,
    borderTopLeftRadius: shape.borderRadius,
})
const avatarContainerStyles = css({
    transform: 'scale(0.75)',
    paddingRight: getThemeSpacing(0.8),
    marginTop: INFO_CONTAINER_SHIFT,
    cursor: 'pointer',
    [minWindowWidth(APP_BREAKPOINTS.xs)]: {
        transform: 'scale(0.85)',
    },
})
const infoContainerStyles = css({
    marginTop: -INFO_CONTAINER_SHIFT,
    width: 'calc(100% - 52px)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    zIndex: 1,
})
const nameStyles = css({
    margin: 0,
    fontSize: 13,
    fontWeight: 800,
    paddingRight: getThemeSpacing(0.5),
    [maxRoomWidth(APP_BREAKPOINTS.sm)]: {
        fontSize: 12,
        paddingRight: getThemeSpacing(0),
    },
    '@media (max-width: 375px)': {
        fontSize: 11,
    },
})
const topicStyles = css({
    fontSize: 11.375,
    fontWeight: 500,
    cursor: 'pointer',
    margin: `${-INFO_CONTAINER_SHIFT}px 0 0`,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    color: palette.contrastText,
    [maxRoomWidth(APP_BREAKPOINTS.md)]: {
        // TODO: this isnt necessarily going to be accurate, as this can be used
        // in the pin area or the room area, but never the content area
        fontSize: 10.375,
        fontWeight: 400,
    },
})
const titleStyles = css({
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
})
const typingIndicatorTextStyles = css({
    fontWeight: 'bold',
})
const actionsStyles = css({
    alignItems: 'center',
    display: 'flex',
    flex: '1 1 100%',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    overflow: 'hidden',
    '& .icon-button': {
        fontSize: '1.04rem',
        padding: 3,
    },
    '& .mfc-button': {
        marginLeft: getThemeSpacing(1),
        transform: 'scale(0.8)',
        [maxPinWidth(APP_BREAKPOINTS.md)]: {
            marginLeft: getThemeSpacing(0.125),
        },
        [maxRoomWidth(APP_BREAKPOINTS.sm)]: {
            marginLeft: getThemeSpacing(0.125),
        },
    },
})

const leftActionsStyles = css({
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    [maxRoomWidth(APP_BREAKPOINTS.sm)]: {
        flex: 'none',
    },
})

const pinnedPmNameStyles = css({
    fontSize: '0.765rem',
    maxWidth: 100,
    overflow: 'hidden',
})

const pinnedPmStyles = css({
    flex: 'unset',
    '& .mfc-button': {
        marginLeft: getThemeSpacing(0.5),
        transform: 'scale(0.9)',
        maxWidth: 28,
        borderRadius: '50%',
        padding: 2,
    },
})

const pinnedMobileActionsStyles = css([
    actionsStyles,
    {
        // marginRight: getThemeSpacing(1),
        [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
            flex: 'none',
        },
        '& .mfc-button': {
            [maxPinWidth(APP_BREAKPOINTS.sm)]: {
                marginLeft: 3,
            },
        },
        '& .icon-button': {
            padding: 4,
        },
    },
])

const linkShareWrapperStyles = css({
    display: 'flex',
    justifyContent: 'flex-end',
    // marginLeft: getThemeSpacing(1),
    [maxRoomWidth(APP_BREAKPOINTS.sm)]: {
        marginLeft: getThemeSpacing(-0.5),
        transform: 'scale(0.8)',
    },
    [maxWindowWidth(375)]: {
        marginLeft: getThemeSpacing(-1.5),
    },
})
const closeIconStyles = css({
    justifyContent: 'flex-end',
    marginLeft: 0,
})
const viewersStyles = css({
    paddingLeft: getThemeSpacing(1),
})
const shareImageStyles = css({
    width: '100%',
    maxWidth: SHARE_IMAGE_WIDTH,
    minWidth: SHARE_IMAGE_WIDTH * 0.9,
    filter: palette.imagesMonochromeFilter,
    '@media (max-width: 375px)': {
        minWidth: SHARE_IMAGE_WIDTH * 0.85,
    },
})

const tipAndShareContainerStyles = css({
    display: 'flex',
    alignItems: 'center',
    [maxRoomWidth(APP_BREAKPOINTS.sm)]: {
        marginLeft: getThemeSpacing(0.25),
    },
})

const tipIconPadding = css({ paddingBottom: 2 })

const friendGreen = css({ '& svg': { color: palette.activeGreen } })

export interface OnlineChatHeaderProps {
    id: number
    isProfile?: boolean
    pinned?: boolean
    preview?: boolean
    showBadge?: boolean
    pinnedPm?: boolean
    pinnedPreview?: boolean
    toggleVideo?: boolean
    videoToggled?: boolean
    room?: boolean
    pm?: boolean
}

const OnlineChatHeader = memo((props: OnlineChatHeaderProps) => {
    const { id } = props
    const { ref, width } = useElementSize()
    const badgeAvatarRef = useRef<HTMLElement>(undefined)
    const { authenticated, isMobile, isIOS, isIpad } = useAppState(
        AppStateType.APP,
    )
    const showChat = useRoomSelector(id, 'showChat')
    const roomExpanded = useFloatingRoomStateSelector('expanded')
    const pinnedRoomCount = usePinnedRoomCount()
    const appPortrait = useScreenStateSelector('portrait')
    const isRoomPinned = useIsRoomPinned(id)

    const isTyping = useUserSelector(props?.id, 'isTypingPM')
    const slug = useUserSelector(id, 'slug')
    const isBookmark = useUserSelector(id, 'isBookmark')
    const isFriend = useUserSelector(id, 'isFriend')
    const isBroadcasting = useUserSelector(id, 'isBroadcasting')
    const isModel = useUserSelector(id, 'isModel')
    const isOffline = useUserSelector(id, 'isOffline')
    const name = useUserSelector(id, 'name')
    const color = useUserSelector(id, 'color')
    const topic = useUserSelector(id, 'topic')
    const viewers = useUserSelector(id, 'viewers')
    const hasNote = useUserSelector(id, 'hasNote')
    const shareAlbums = useUserSelector(id, 'shareAlbums') ?? 0
    const shareThings = useUserSelector(id, 'shareThings') ?? 0
    const shareTipmenus = useUserSelector(id, 'shareTipmenus') ?? 0
    const shareRecordings = useUserSelector(id, 'shareRecordings') ?? 0

    const {
        preview = false,
        showBadge = false,
        pinned = false,
        pinnedPm = false,
        pinnedPreview = false,
        isProfile = false,
        toggleVideo = false,
        videoToggled = false,
        room = false,
        pm = false,
    } = safeObj(props)
    const isMessage = pm || pinnedPm
    const isMobileDevice = isMobile || isIOS || isIpad
    const isPinnedMobile =
        (isMobileDevice || width < APP_BREAKPOINTS.s) && pinned

    const modelColor = useModelColor(color)

    const hasAlbums = shareAlbums > 0
    const hasThings = shareThings > 0
    const hasTipMenus = shareTipmenus > 0
    const hasRecordings = shareRecordings > 0
    const hasShareContent =
        hasAlbums || hasRecordings || hasThings || hasTipMenus

    const showShareContent =
        hasShareContent && !preview && !isPinnedMobile && !pinnedPm
    const showShareTipButton = authenticated && hasTipMenus
    const showShareButton =
        !isProfile &&
        !isPinnedMobile &&
        (isMobileDevice ||
            (width > APP_BREAKPOINTS.s && !pinned) ||
            (pinned && width > APP_BREAKPOINTS.s + 30))

    const resolvedRootStyles =
        preview || pinnedPm ? [rootStyles, rootPreviewStyles] : [rootStyles]

    const resolvedActionsStyles = pinned
        ? [pinnedMobileActionsStyles]
        : [actionsStyles]

    const resolvedNameStyles = [nameStyles]
    if (pinnedPm) {
        resolvedActionsStyles.push(pinnedPmStyles)
        resolvedNameStyles.push(pinnedPmNameStyles)
    }

    const onOpenMenu = useCallback(() => {
        openMenuDialog(slug, {
            room: !pinned && !preview,
            transitionSource: room
                ? TransitionTypes.ROOM
                : preview || pinnedPm
                  ? TransitionTypes.FLOATER
                  : pinned
                    ? TransitionTypes.PINROOM
                    : TransitionTypes.NONE,
        })
    }, [slug])

    const onToggleFriend = useCallback(() => {
        if (!authenticated) openAuthenticationDrawer()
        else if (isFriend)
            openFriendConfirmationDrawer(slug, ConfirmationType.Friend)
        else addFriend(slug)
    }, [authenticated, isFriend, slug])

    const onToggleBookmark = useCallback(() => {
        if (!authenticated) openAuthenticationDrawer()
        else if (isBookmark)
            openFriendConfirmationDrawer(slug, ConfirmationType.Bookmark)
        else bookmarkModel(slug)
    }, [authenticated, isBookmark, slug])

    const onOpenNotes = useCallback(() => {
        if (!authenticated) {
            openAuthenticationDrawer()
            return
        }
        openNotesDrawer(slug)
    }, [authenticated, slug])

    const onTogglePinnedRoomShowChat = useCallback(() => {
        togglePinRoomChat(id)
    }, [id])

    const onOpenTopic = useCallback(() => openTopicDrawer(slug), [slug])
    const onPinClick = useCallback(
        () => pinRoom(id, { pinned: !pinned }),
        [id, pinned],
    )
    const onMinimizeClick = useCallback(
        () =>
            motionTransition(() => minimizeFloater(id, QUICK_REPLY, true), {
                id,
                transitionSource: TransitionTypes.FLOATER,
                transitionDestination: TransitionTypes.NAV,
            }),
        [id],
    )
    const onCloseFloater = useCallback(
        () => closeFloater(id, QUICK_REPLY),
        [id],
    )
    const onVideoToggle = useCallback(() => toggleQuickReplySize(id), [id])
    const onRoomClick = useCallback(
        () =>
            openRoom(id, {
                pinned,
                transitionSource: room
                    ? TransitionTypes.ROOM
                    : preview || pinnedPm
                      ? TransitionTypes.FLOATER
                      : pinned
                        ? TransitionTypes.PINROOM
                        : TransitionTypes.NONE,
            }),
        [id, pinned, room, preview, pinnedPm],
    )
    const onPmClick = useCallback(() => {
        minimizeFloater(id, QUICK_REPLY, true)
        openMessage(slug)
    }, [id])
    const onOpenTip = useCallback(() => {
        openTipsDrawer({
            slug,
            hiddenShows: true,
            chatError: true,
            pinned,
        })
    }, [slug, pinned])
    const onPin = useCallback(() => {
        motionTransition(() => pinRoom(id, { pinned: !isRoomPinned }), {
            id,
            transitionSource: TransitionTypes.FLOATER,
            transitionDestination: TransitionTypes.PINROOM,
        })
    }, [id, isRoomPinned])
    if (!id) return null

    const pinnedCanShowBookmarkFriend = !pinned || width > 360
    return (
        <div
            id={`chat-header-${id}`}
            className="chat-header"
            ref={ref}
            css={resolvedRootStyles}
        >
            <div
                css={avatarContainerStyles}
                data-testid="Container__ModelAvatar"
                onClick={onOpenMenu}
            >
                <BadgeAvatar
                    disableBadge={!showBadge}
                    ref={badgeAvatarRef}
                    offline={isOffline}
                    alt={name}
                    size={40}
                    id={id}
                />
            </div>
            <div css={infoContainerStyles}>
                <div css={titleStyles}>
                    <h2
                        style={{
                            color: modelColor,
                        }}
                        css={resolvedNameStyles}
                        data-testid="Typography__ModelName"
                        id={`name-${id}`}
                    >
                        {name}
                    </h2>
                    <div
                        css={resolvedActionsStyles}
                        data-testid="Container__FriendsBookmarks"
                    >
                        <div css={leftActionsStyles}>
                            {!pinned && !pinnedPm && (
                                <>
                                    <IconButton
                                        onClick={onToggleFriend}
                                        title={
                                            isFriend ? 'Unfriend' : 'Add Friend'
                                        }
                                        css={isFriend ? friendGreen : null}
                                    >
                                        {isFriend ? (
                                            <Friended />
                                        ) : (
                                            <AddFriend />
                                        )}
                                    </IconButton>
                                    <IconButton
                                        onClick={onToggleBookmark}
                                        title={
                                            isBookmark
                                                ? 'Unbookmark'
                                                : 'Bookmark'
                                        }
                                        data-testid="ChatHeader__IconButton__Bookmark"
                                    >
                                        {isBookmark ? (
                                            <Bookmarked />
                                        ) : (
                                            <AddBookmark />
                                        )}
                                    </IconButton>
                                    {(!preview ||
                                        width >= APP_BREAKPOINTS.sm) && (
                                        <IconButton
                                            onClick={onOpenNotes}
                                            data-testid="ChatHeader__IconButton__Notes"
                                        >
                                            <Notes filled={hasNote} />
                                        </IconButton>
                                    )}
                                </>
                            )}
                            {pinned && pinnedCanShowBookmarkFriend && (
                                <>
                                    <IconButton
                                        onClick={onToggleFriend}
                                        title={
                                            isFriend ? 'Unfriend' : 'Add Friend'
                                        }
                                        css={isFriend ? friendGreen : null}
                                    >
                                        {isFriend ? (
                                            <Friended />
                                        ) : (
                                            <AddFriend />
                                        )}
                                    </IconButton>
                                    <IconButton
                                        title="Open Room"
                                        onClick={onToggleBookmark}
                                        data-testid="ChatHeader__IconButton__Bookmark"
                                    >
                                        {isBookmark ? (
                                            <Bookmarked />
                                        ) : (
                                            <AddBookmark />
                                        )}
                                    </IconButton>
                                    {/* {!isMobileDevice && width > 800 && ( */}
                                    <IconButton
                                        onClick={onOpenNotes}
                                        title="Open Notes"
                                        data-testid="ChatHeader__IconButton__Notes"
                                    >
                                        <Notes filled={hasNote} />
                                    </IconButton>
                                    {/* )} */}
                                </>
                            )}
                            {preview && !!viewers && (
                                <div
                                    css={viewersStyles}
                                    data-testid="Preview__Viewers"
                                >
                                    <CounterViewers
                                        viewers={viewers}
                                        highContrast
                                    />
                                </div>
                            )}
                            {(pinned || pinnedPm) && (
                                <>
                                    {pinnedPm &&
                                        !isOffline &&
                                        isBroadcasting &&
                                        !isMobile &&
                                        toggleVideo && (
                                            <IconButton
                                                title="Toggle Video"
                                                onClick={onVideoToggle}
                                            >
                                                <Videocam
                                                    active={videoToggled}
                                                />
                                            </IconButton>
                                        )}
                                    {pinnedPm ? (
                                        <IconButton
                                            title="Open Full PM"
                                            onClick={onPmClick}
                                        >
                                            <MessageIcon />
                                        </IconButton>
                                    ) : (
                                        <IconButton
                                            title="Open Room"
                                            onClick={onRoomClick}
                                        >
                                            <RoomIcon />
                                        </IconButton>
                                    )}
                                    {pinned &&
                                        (width >
                                            PINNED_ROOM_MIN_WIDTH_FOR_CHAT ||
                                            showChat) && (
                                            <IconButton
                                                title={`${showChat ? 'Hide' : 'Show'} Pinned Room Chat`}
                                                data-testid="PinRoomHelper__ShowChatButton"
                                                onClick={
                                                    onTogglePinnedRoomShowChat
                                                }
                                            >
                                                {showChat ? (
                                                    <HideChat size={1} />
                                                ) : (
                                                    <ShowChat size={1} />
                                                )}
                                            </IconButton>
                                        )}
                                </>
                            )}
                            {isModel && pinned && (
                                <IconButton
                                    title={i18n.t('chat.tip')}
                                    data-testid="ChatHeader__IconButton__Tip"
                                    onClick={onOpenTip}
                                >
                                    <TipIcon filled css={tipIconPadding} />
                                </IconButton>
                            )}
                            {!pinned && !pinnedPm && !isMobileDevice && (
                                <ButtonTipText onClick={onOpenTip} />
                            )}
                            {showShareContent && showShareTipButton && (
                                <ButtonShareTip name={name} />
                            )}
                        </div>
                        <div css={tipAndShareContainerStyles}>
                            {showShareContent && showShareButton && (
                                <div css={linkShareWrapperStyles}>
                                    <a //TODO: use a LinkButton
                                        href={`https://share.myfreecams.com/${slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="Link__MFCShare"
                                    >
                                        <img
                                            css={shareImageStyles}
                                            data-testid="Image__MFCShare"
                                            src="https://img.mfcimg.com/files/images/misc/MFCShare.svg"
                                        />
                                    </a>
                                </div>
                            )}
                        </div>
                        {!pinnedPm &&
                            !pinned &&
                            !preview &&
                            !isMobile &&
                            pinnedRoomCount > 0 &&
                            !appPortrait && (
                                <IconButton
                                    title={`${roomExpanded ? 'Show' : 'Hide'} Pinned Rooms`}
                                    onClick={() =>
                                        setFullScreenExpandedState(
                                            !roomExpanded,
                                        )
                                    }
                                >
                                    {roomExpanded ? (
                                        <ArrowCollapseLeft />
                                    ) : (
                                        <ArrowExpandRight />
                                    )}
                                </IconButton>
                            )}
                        {(pinned || pinnedPm) && (
                            <>
                                {!pinnedPm && (
                                    <IconButton
                                        title="Close Room"
                                        onClick={onPinClick}
                                        css={closeIconStyles}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                )}
                                {pinnedPm && (
                                    <>
                                        <IconButton
                                            title="Minimize Quick Reply"
                                            onClick={onMinimizeClick}
                                        >
                                            <ChevronDownIcon />
                                        </IconButton>
                                        <ButtonX
                                            title="Close Quick Reply"
                                            onClick={onCloseFloater}
                                            size={1}
                                        />
                                    </>
                                )}
                            </>
                        )}
                        {preview && !room && pinnedPreview && (
                            <PinButton
                                filled={isRoomPinned}
                                title={`${isRoomPinned ? 'Unpin' : 'Pin'} Model`}
                                onClick={onPin}
                                size={1}
                                color={palette.contrastIcons}
                            />
                        )}
                    </div>
                </div>
                <p
                    css={topicStyles}
                    data-testid="Typography__Topic"
                    onClick={onOpenTopic}
                    id={`blurb-${id}`}
                >
                    {isMessage && isTyping && (
                        <span
                            css={typingIndicatorTextStyles}
                            data-testid="OnlineChatHeader__TypingIndicatorText"
                        >
                            is typing...{' '}
                        </span>
                    )}
                    {!pinnedPm && topic}
                </p>
            </div>
        </div>
    )
})

export default OnlineChatHeader
